// ./components/ModeSwitchWithFeedback.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

type ModeSwitchWithFeedbackProps = {
  label?: string;
  value: boolean;
  onChange: (next: boolean) => void;
  triggerCount?: number; // toggles before opening modal (default 5)
};

const EMOJIS = ["😞", "🙁", "😐", "🙂", "😊"];

const ModeSwitchWithFeedback: React.FC<ModeSwitchWithFeedbackProps> = ({
  label = "Switch Editor",
  value,
  onChange,
  triggerCount = 5,
}) => {
  const [toggleCount, setToggleCount] = useState(0);
  const [open, setOpen] = useState(false);

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const userInitiatedRef = useRef(false);

  const handleSwitchChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    userInitiatedRef.current = true;
    onChange(checked);
  };

  useEffect(() => {
    if (!userInitiatedRef.current) return;
    userInitiatedRef.current = false;

    setToggleCount((c) => {
      const next = c + 1;
      if (next >= triggerCount) {
        setOpen(true);
        return 0;
      }
      return next;
    });
  }, [value, triggerCount]);

  const resetModal = () => {
    setRating(null);
    setComment("");
    setSubmitted(false);
  };

  const closeModal = () => {
    setOpen(false);
    resetModal();
  };

  const handleSubmit = () => {
    // send rating/comment to your API here if desired
    setSubmitted(true);
  };

  // Show comment box only if rating is 1 or 2 (bad or very bad)
  const shouldShowComment = rating === 1 || rating === 2;

  // Send button requires at least a rating
  const canSend = useMemo(() => rating !== null, [rating]);

  return (
    <>
      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: "0.9rem", color: "#444" }}>{label}</span>
        <Switch
          checked={value}
          onChange={handleSwitchChange}
          color="warning"
          size="small"
        />
      </label>

      <Dialog
        open={open}
        onClose={closeModal}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: "94vw",
            borderRadius: 3,
            boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
          },
        }}
      >
        {!submitted ? (
          <>
            <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
              Rate consent section
              <IconButton
                onClick={closeModal}
                size="small"
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  Emoji row (tap to rate):
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  justifyContent="space-between"
                  sx={{ px: 0.5 }}
                >
                  {EMOJIS.map((emoji, idx) => {
                    const active = rating === idx + 1;
                    return (
                      <Tooltip
                        title={["Very bad", "Bad", "Okay", "Good", "Great"][idx]}
                        key={emoji}
                      >
                        <Button
                          onClick={() => setRating(idx + 1)}
                          sx={{
                            minWidth: 48,
                            minHeight: 48,
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            fontSize: 28,
                            lineHeight: 1,
                            p: 0,
                            border: active ? "2px solid #ff9500" : "1px solid #e5e7eb",
                            bgcolor: active ? "rgba(255,149,0,0.08)" : "background.paper",
                            "&:hover": {
                              bgcolor: active ? "rgba(255,149,0,0.12)" : "grey.50",
                            },
                          }}
                        >
                          {emoji}
                        </Button>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>

              {shouldShowComment && (
                <>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    How can we improve?
                  </Typography>
                  <TextField
                    placeholder="Add your comments"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                      mb: 2,
                    }}
                  />
                </>
              )}

              <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                <Button onClick={closeModal} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!canSend}
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    px: 1.75,
                    py: 0.75,
                    bgcolor: "#ff9500",
                    color: "black",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#e68600" },
                  }}
                >
                  Send
                </Button>
              </Stack>
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
              <span role="img" aria-label="heart" style={{ marginRight: 8 }}>
                💙
              </span>
              Thanks for your feedback!
              <IconButton
                onClick={closeModal}
                size="small"
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                We appreciate you taking the time to help us improve the new consent section.
              </Typography>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={closeModal}
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    px: 1.75,
                    py: 0.75,
                    bgcolor: "#ff9500",
                    color: "black",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#e68600" },
                  }}
                >
                  Close
                </Button>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default ModeSwitchWithFeedback;
